import type { CandidateArticle } from "../../../../../api/types"
import type { Verdict } from "../../studio/studioTypes"
import type { StatementCard } from "../useStudioV2State"

export interface ApprovedSnapshotFramework {
  key: string
  name: string
  /** Worst verdict over the framework's covered articles; null = no statement
   *  reached any article. */
  summary: Verdict | null
  /** articleNumber -> verdict, only for articles a live statement covers. */
  byArticle: Record<string, Verdict>
}

export interface ApprovedSnapshotArticle {
  number: string
  name: string
  supplierCode?: string
}

/** What the approval dialog's detail matrix renders: taken from the studio
 *  state at the moment of approval (the read model is frozen afterwards). */
export interface ApprovedSnapshot {
  frameworks: ApprovedSnapshotFramework[]
  articles: ApprovedSnapshotArticle[]
}

/** Severity order for collapsing several statements on one article/framework
 *  cell: the strictest reading wins, so a single Nicht konform is never
 *  hidden behind a Konform statement on the same article. */
const SEVERITY: Record<Verdict, number> = {
  non_compliant: 4,
  compliant_with_conditions: 3,
  compliant: 2,
  unknown: 1,
  pending: 0,
}

function worse(a: Verdict | undefined, b: Verdict): Verdict {
  if (!a) return b
  return SEVERITY[b] > SEVERITY[a] ? b : a
}

export function buildApprovedSnapshot(input: {
  frameworks: string[]
  nameOf: (fw: string) => string
  cardsByFramework: Map<string, StatementCard[]>
  pool: CandidateArticle[]
}): ApprovedSnapshot {
  // A scopeAll statement whose resolution is EMPTY still writes the whole pool:
  // approve's never-stuck fallback (`approve_declaration`, "a live statement
  // with scope_all=True asserts all articles of this supplier") expands exactly
  // such a statement to the supplier pool. The reasons the resolution is empty
  // are real and recurring: M2 declined a blanket it could not ground in a
  // passage (LQ / Hornung, 2026-09-09: "sämtliche in der Excel-Datei
  // aufgeführten Artikel" on 6 of 10 Verordnungen), and a manually added
  // statement never gets a resolution at all. Rendering "Kein Artikelbezug"
  // for those told the reviewer nothing was written while 7 articles were.
  const poolNumbersList = input.pool.map((a) => a.articleNumber)
  const coveredBy = (card: StatementCard): readonly string[] =>
    card.scopeAll && card.resolvedArticles.length === 0
      ? poolNumbersList
      : card.resolvedArticles
  const frameworks = input.frameworks.map((key) => {
    const byArticle: Record<string, Verdict> = {}
    for (const card of input.cardsByFramework.get(key) ?? []) {
      if (card.excluded || !card.verdict) continue
      for (const n of coveredBy(card)) {
        byArticle[n] = worse(byArticle[n], card.verdict)
      }
    }
    let summary: Verdict | null = null
    for (const v of Object.values(byArticle))
      summary = worse(summary ?? undefined, v)
    return { key, name: input.nameOf(key), summary, byArticle }
  })
  // Only the articles this declaration actually reached: the pool is the
  // supplier's WHOLE article list, and rendering all of it produced dozens
  // of empty rows around the updated ones (product-owner screenshot,
  // 28.08., sandbox Henkel: 2 written, ~30 listed). Pool order first, then
  // covered numbers the pool no longer carries as bare rows: a statement's
  // resolved numbers can be a SUPERSET of the current pool (supplier
  // re-match, pool re-derivation), which is why ArticleSlot and
  // ReviewMaskPane build pool UNION card numbers, never the intersection
  // (claude-bot review, #2042: a pure intersection could hide the whole
  // detail section at approval time).
  const covered = new Set(frameworks.flatMap((f) => Object.keys(f.byArticle)))
  const inPool = input.pool
    .filter((a) => covered.has(a.articleNumber))
    .map((a) => ({
      number: a.articleNumber,
      name: a.name,
      supplierCode: a.supplierCode,
    }))
  const poolNumbers = new Set(input.pool.map((a) => a.articleNumber))
  const bare = [...covered]
    .filter((n) => !poolNumbers.has(n))
    .sort()
    .map((n) => ({ number: n, name: "" }))
  const articles = [...inPool, ...bare]
  return { frameworks, articles }
}
